const _filterOperators = {
  '=': 'equals',
  '<>': 'does not equal',
  '>': 'is greater than',
  '<': 'is less than',
  ILIKE: 'contains',
  'NOT ILIKE': 'does not contain',
  '~*': 'matches regex',
  '~': 'matches regex',
  '!~*': 'does not match regex',
  '&': 'Bitwise AND (&)',
  misc_flags_set: 'matches Ultimate Exit',
  misc_flags_not_set: 'does not match Ultimate Exit',
  pdb_common: 'common with mine',
  pdb_not_common: 'not common with mine',
  field_equals: 'equals the value of',
  field_not_equal: 'does not equal the value of'
};

// it's slightly concerning that there are other operators which are NOT mapped by filterOperators
export type Operator = keyof typeof _filterOperators | 'bitwiseNone' | '!~' | '<<=';
export type OperatorRecord = Readonly<Partial<Record<Operator, string>>>;
export type OperatorsDictionary = Partial<Record<string, Partial<Record<Operator, string>>>>;

export const filterOperators: OperatorRecord = _filterOperators;

export const intOperators: OperatorRecord = {
  '=': 'equals',
  '<>': 'does not equal',
  '>': 'is greater than',
  '<': 'is less than'
};
export const stringOperators: OperatorRecord = {
  '=': 'equals',
  '<>': 'does not equal',
  ILIKE: 'contains',
  'NOT ILIKE': 'does not contain'
};
export const inetOperators: OperatorRecord = {
  ILIKE: 'equals',
  '<>': 'does not equal'
};
export const caseSensitiveRegexOperators: OperatorRecord = {
  '~': 'matches regex',
  '!~': 'does not match regex'
};
export const regexOperators: OperatorRecord = {
  '~*': 'matches regex',
  '!~*': 'does not match regex'
};

export const stringAndRegexOperators: OperatorRecord = {
  ILIKE: 'contains',
  'NOT ILIKE': 'does not contain',
  '~*': 'matches regex',
  '!~*': 'does not match regex'
};

export function isStringOrRegexOperator(operator: string): boolean {
  return operator in stringAndRegexOperators;
}

export const allRegexOperators: OperatorRecord = {
  '~': 'matches regex (case sens)',
  '~*': 'matches regex',
  '!~*': 'does not match regex'
};
export const standardFilterOperators: OperatorRecord = {
  '=': 'equals',
  '<>': 'does not equal'
};
export const rightFilterFieldOperators: OperatorRecord = {
  field_equals: 'equals the value of',
  field_not_equal: 'does not equal the value of'
};

// Friendly operator names (e.g. from Kaizen/NLQ tools) mapped to their raw SQL token. Shared by
// FiltersHelper (traffic/events) and synthUtils (synthetics) to avoid duplicated maps.
export const friendlyOperatorAliases: Readonly<Record<string, string>> = {
  equals: '=',
  notEquals: '<>',
  '!=': '<>',
  bitwiseAnd: '&',
  bitwiseOr: '|',
  lessThan: '<',
  greaterThan: '>',
  lessThanOrEquals: '<=',
  greaterThanOrEquals: '>=',
  notContains: 'NOT ILIKE',
  contains: 'ILIKE',
  notContainsRe: '!~',
  containsRe: '~',
  notContainsReStar: '!~*',
  containsReStar: '~*',
  field_equals: '=',
  field_not_equal: '<>'
};

export const filterConnectors: Readonly<Record<string, 'AND' | 'OR'>> = {
  All: 'AND',
  Any: 'OR',
  all: 'AND',
  any: 'OR'
};

const sqlSafeOperators = new Set([
  '=',
  '<>',
  '<',
  '>',
  '<=',
  '>=',
  'ILIKE',
  'NOT ILIKE',
  'LIKE',
  'NOT LIKE',
  '~',
  '~*',
  '!~',
  '!~*',
  '&',
  '|',
  'bitwiseNone',
  'misc_flags_set',
  'misc_flags_not_set',
  'pdb_common',
  'pdb_not_common',
  '<<='
]);

export function isSqlSafeOperator(operator: string): boolean {
  return sqlSafeOperators.has(operator);
}

export function resolveSqlOperator(operator: string): string | null {
  const resolved = friendlyOperatorAliases[operator] ?? operator;
  return isSqlSafeOperator(resolved) ? resolved : null;
}

// Postgres dollar-quote tag used by FiltersHelper/synthUtils to safely quote SQL string literals
// without escaping - shared so both stay in sync on the exact sentinel used.
const KENTIK_PREFIX_TAG = '$_kntq$';

export function kentikPrefixWrap(value: string): string {
  return `${KENTIK_PREFIX_TAG}${value}${KENTIK_PREFIX_TAG}`;
}

export function isKentikPrefixWrapped(value: string): boolean {
  return value.startsWith(KENTIK_PREFIX_TAG) && value.endsWith(KENTIK_PREFIX_TAG);
}

export function unwrapKentikPrefix(value: string): string {
  return value.slice(KENTIK_PREFIX_TAG.length, value.length - KENTIK_PREFIX_TAG.length);
}

export const parametricOperators: Readonly<Partial<Record<string, Operator>>> = {
  market: '=',
  as_name: 'ILIKE',
  as_number: '=',
  bgp_aspath: '~',
  bgp_community: '~',
  city: 'ILIKE',
  cdn: 'ILIKE',
  connectivity_type: '=',
  country: '=',
  custom_dimension: '=',
  eth_mac: '=',
  device: '=',
  device_label: '=',
  dns_query: 'ILIKE',
  service_name: 'ILIKE',
  service_type: 'ILIKE',
  service_provider: 'ILIKE',
  ott_classification_type: '=',
  http_host_header: 'ILIKE',
  http_status: '=',
  http_url: 'ILIKE',
  interface_desc: 'ILIKE',
  interface_name: 'ILIKE',
  ip: 'ILIKE',
  network_boundary: '=',
  provider: '=',
  port: '=',
  region: 'ILIKE',
  site: 'ILIKE',
  site_market: 'ILIKE',
  tag: 'ILIKE',
  vlan: '=',
  cloud_provider: '=',
  gce_proj_id: '=',
  gce_vm_name: 'ILIKE',
  gce_region: '=',
  gce_zone: 'ILIKE',
  gce_vpn_snn: 'ILIKE',
  gce_vm_type: '=',
  aws_acc_id: '=',
  aws_vm_name: 'ILIKE',
  aws_vm_id: '=',
  aws_region: 'ILIKE',
  aws_zone: 'ILIKE',
  aws_zone_id: '=',
  aws_vm_type: '=',
  aws_pub_dns: 'ILIKE',
  aws_priv_dns: 'ILIKE',
  aws_vpc_id: '=',
  aws_subnet_id: '=',
  az_inst_id: 'ILIKE',
  az_inst_name: 'ILIKE',
  az_region: 'ILIKE',
  az_vnet: 'ILIKE',
  az_sub_id: 'ILIKE',
  az_rsrc_grp: 'ILIKE'
};
